/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import styles from './AltYMMEContentBlock.module.scss';
import cx from 'classnames';
import { useLegacyRouter } from '@/hooks/useLegacyRouter';
import logger from '@/utils/logger';
import { YMMEModal } from '../YMMEModal';
import { useCallback, use, useState } from 'react';
import { useLocale } from '@/hooks/useLocale';
import { countryCodes } from '@/constants/locale';
import { useLabels } from '@/hooks/useLabels';
import { SnackBarContext } from '@/components/SnackBarContext';
import { usePreferredVehicle } from '../../../header/hooks/usePreferredVehicle';
import { trackShopWithoutVehicle } from '@/utils/analytics/track/trackShopWithoutVehicle';
import { useDeleteDefaultVehicleMutation } from '../../api/deleteDefaultVehicle';
import { setInteractionLocation } from '../../utils/ymmeLocationHelper';
import type { InteractionLocation } from '@/types/analytics';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useRedirectAfterVehicleChanged } from '@/features/category/hooks/useRedirectAfterVehicleChanged';
import { Skeleton, View } from '@az/starc-ui';
import { VehicleSelectedYMME } from './VehicleSelectedYMME';
import { MoreInfoNeededInlineYMME } from './MoreInfoNeededInlineYMME';
import { useAcesFlags } from '../../utils/acesFlags';
import { useDeleteDefaultVehicleAcesMutation } from '../../apiAces/deleteDefaultVehicleACES';
import ContentContainer from '@/components/AZCustomComponent/ContentContainer';
import { useProductList } from '@/features/shelf/context/useProductList';
import { usePageType } from '@/features/category/api/getPageType';
import { pageTypes } from '@/constants/page';
import { YMMEInfoContext } from '../YMMEModal/context/YMMEInfoContext';
import { useProductDetails } from '@/features/product/api/getProductDetails';
import { type ProductModelACES } from '@/api/types/products-types';
import { NonVehicleYMMERedesign } from './NonVehicleYMMERedesign';

type Props = {
  pageType?: string;
  onUpdateVehicleSuccess?: (() => void) | undefined;
  nonShopPage?: boolean;
  removeMargins?: boolean;
};

const labelMap = {
  lblAddVehicle: 'button_YMME_Body_AddVehicle',
  lblAddMakeModel: 'add_make_model',
  yourLabel: 'label_MyAccountMyProfile_landing_RewardYour',
  hasBeenMovedLabel: 'label_YMME_Body_HasBeenMovedTo',
  lblMakeModel: 'label_MakeModel',
  lblAddYear: 'label_Add_Year',
};

export default function AltYMMEContentBlock({
  pageType,
  onUpdateVehicleSuccess,
  nonShopPage = false,
  removeMargins = false,
}: Props) {
  const router = useLegacyRouter();
  const labels = useLabels(labelMap);
  const locale = useLocale();
  const [showYmme, setShowYmme] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState<string>(labels.lblMakeModel);
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const { acesEnabled } = useAcesFlags();
  const { setVehicleQuestionsYMMEOpen } = use(YMMEInfoContext);

  const { data: shelfData } = useProductList();
  const productFinderData = shelfData?.productFinderData;
  const productFilterData = shelfData?.productFilterData;
  const productRefinementIsAfterYMME = productFinderData?.isAfterYmme || !!productFilterData;

  const { data: pageTypeData, isLoading: isPageTypeDataLoading } = usePageType();
  const isPDP = router.pathname === '/products/[...slug]';
  const isCategoryPage = pageTypeData?.pageType === pageTypes.CategoryGrouping;
  const isBrowsePageType = router.route === '/[...seoUrlPath]';

  const openAQYMMECallback = useCallback(() => {
    setInteractionLocation('needsMoreInfo');
    setVehicleQuestionsYMMEOpen(true);
    setShowYmme(true);
  }, [setVehicleQuestionsYMMEOpen]);

  const openDefaultYMME = useCallback(() => {
    setInteractionLocation((pageType ?? 'home') as InteractionLocation);
    setVehicleQuestionsYMMEOpen(false);
    setShowYmme(true);
  }, [pageType, setVehicleQuestionsYMMEOpen]);
  const { mutateAsync: removeDefaultVehicle } = useDeleteDefaultVehicleMutation();
  const { mutateAsync: removeDefaultAcesVehicle } = useDeleteDefaultVehicleAcesMutation();
  const { data: productData } = useProductDetails({ enabled: false });
  const fitment = (productData?.product as ProductModelACES)?.vehicleFit;
  const { showSnackBar } = use(SnackBarContext);
  const preferredVehicle = usePreferredVehicle();
  const redirectAfterVehicleChanged = useRedirectAfterVehicleChanged();

  const partGroupIDs = (() => {
    if (isPDP) {
      return productData?.product?.partGroupId ? [productData.product.partGroupId] : [];
    }
    return [];
  })();

  const shouldShowMoreInfoNeededInlineYmme = isPDP && fitment === 'NEEDS_MORE_INFO' && acesEnabled;

  const isMX = locale === countryCodes.mx;
  const isBR = locale === countryCodes.ptBr;

  const handleOpenYmme = (method: string) => {
    setInteractionLocation((pageType ?? 'home') as InteractionLocation);
    setVehicleQuestionsYMMEOpen(false);
    setSelectedMethod(method);
    setShowYmme(true);
  };

  const openMakeModelYMME = () => {
    handleOpenYmme(labels.lblMakeModel);
  };

  const closeYmme = () => {
    setShowYmme(false);
  };

  const removeVehicle = () => {
    try {
      if (acesEnabled) {
        void removeDefaultAcesVehicle();
      } else {
        void removeDefaultVehicle();
      }
    } catch (err) {
      logger.error({
        message: 'Error when removing default vehicle',
        meta: {
          error: err,
        },
      });
    } finally {
      onUpdateVehicleSuccess?.();
    }
  };

  const handleShopWithoutVehicle = () => {
    setInteractionLocation((pageType || 'home') as InteractionLocation);
    removeVehicle();
    const snackMessage = preferredVehicle?.vehicleName
      ? `${labels.yourLabel} ${preferredVehicle.vehicleName} ${labels.hasBeenMovedLabel}`
      : '';
    showSnackBar(snackMessage);
    trackShopWithoutVehicle();
  };

  if (isPageTypeDataLoading && isCategoryPage) {
    return (
      <View className={styles.removeHorizontalPadding} height={isMobile ? '100%' : '115px'}>
        <Skeleton height="100%" width="100%" />
      </View>
    );
  }

  return (
    <ContentContainer withMargin={!removeMargins && !productRefinementIsAfterYMME}>
      <div
        className={cx(styles.container, {
          [styles.addVehicleContainer]: !preferredVehicle,
          [styles.nonShopContainer]: nonShopPage,
          [styles.pdp]: pageType === 'ProductDetail',
          [styles.nonUSContainer]: isMX || isBR,
          [styles.moreInfoNeededContainer]:
            !!preferredVehicle && shouldShowMoreInfoNeededInlineYmme,
          [styles.hasVehicleContainer]: !!preferredVehicle && !shouldShowMoreInfoNeededInlineYmme,
          [styles.searchResults]: router.route === '/searchresult',
        })}
        data-testid="ymme-top-container"
      >
        {preferredVehicle ? (
          shouldShowMoreInfoNeededInlineYmme ? (
            <MoreInfoNeededInlineYMME
              preferredVehicle={preferredVehicle}
              openAQYMME={openAQYMMECallback}
              setShowYmme={openDefaultYMME}
            />
          ) : (
            <VehicleSelectedYMME
              preferredVehicle={preferredVehicle}
              setShowYmme={setShowYmme}
              handleShopWithoutVehicle={handleShopWithoutVehicle}
            />
          )
        ) : (
          <NonVehicleYMMERedesign openDefaultYMME={openMakeModelYMME} />
        )}
        <YMMEModal
          onUpdateVehicleSuccess={() => {
            if (isBrowsePageType) {
              void redirectAfterVehicleChanged();
            }
            closeYmme();
          }}
          showYmme={showYmme}
          partGroupId={partGroupIDs[0]}
          closeYmmeWidget={closeYmme}
          pageType={pageType}
          selectedMethod={acesEnabled && !!preferredVehicle?.vehicleName ? '' : selectedMethod}
        />
      </div>
    </ContentContainer>
  );
}
